import { File as FileText, Loader2, X } from "lucide-react";
import UploadItem from "@/shared/models/UploadItem";
import { useUploadDocument } from "../api/useUploadDocument";
import { useEffect } from "react";
import { errorNotification } from "@/shared/utils/Notifications";

interface AddedFileProps {
  file: UploadItem;
  onItemRemove: (id: number) => void;
  onItemStatusUpdate: (item: UploadItem) => void;
}

export function AddedFile({
  file,
  onItemRemove,
  onItemStatusUpdate,
}: AddedFileProps) {
  const uploadFailedMessage = "Upload failed";

  const {
    uploadDocument,
    isLoading: isUploading,
    isFailed: isUploadFailed,
  } = useUploadDocument();

  const updateFileStatus = (
    status: typeof file.status,
    uploadedId?: string,
    error?: string,
  ) => {
    file.status = status;
    if (uploadedId) file.uploadedId = uploadedId;
    if (error) file.error = error;
    onItemStatusUpdate(file);
  };

  useEffect(() => {
    const upload = async () => {
      if (file.file && !file.uploadedId) {
        try {
          updateFileStatus("uploading");
          const uploadedId = (await uploadDocument(file.file)).id;
          if (uploadedId) updateFileStatus("complete", uploadedId);
          else updateFileStatus("error", undefined, uploadFailedMessage);
        } catch (_error) {
          updateFileStatus("error", undefined, uploadFailedMessage);
          errorNotification(file.name, uploadFailedMessage);
        }
      }
    };

    if (file.status === "toProcess") {
      upload();
    } else if (file.uploadedId && file.status !== "complete") {
      updateFileStatus("complete", file.uploadedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const getBackgroundColor = () => {
    if (isUploadFailed || file.status === "error") return "bg-red-50";
    return "bg-purple-50";
  };

  return (
    <div
      key={file.id}
      className={`relative flex items-center ${getBackgroundColor()} text-purple-700 px-4 py-3 rounded-lg min-w-0`}
    >
      {isUploading || file.status === "uploading" ? (
        <Loader2 className="h-8 w-8 flex-shrink-0 animate-spin" />
      ) : (
        <FileText className="h-8 w-8 flex-shrink-0" />
      )}
      <div className="flex flex-col ml-2">
        <span className="font-medium break-all">{file.name}</span>
        {isUploadFailed || file.status === "error" ? (
          <span className="text-sm text-red-500">
            {file.error || uploadFailedMessage}
          </span>
        ) : (
          file.size && (
            <span className="text-sm text-purple-500">{file.size}</span>
          )
        )}
      </div>
      <button
        onClick={() => onItemRemove(file.id)}
        className="absolute top-1 right-1 text-purple-500 hover:text-purple-700 p-1"
      >
        <X className="h-4 w-4 flex-shrink-0" />
      </button>
    </div>
  );
}
